// src/common/ultraBridge.js
import { getActiveTypeHeader } from "common/activeTypeGate";
const pending = new Map();               // requestId -> {resolve, reject}
const waitQueueByType = new Map();       // legacy용: type별 대기중 requestId 큐
const listeners = new Map();             // type -> Set<handler>
let inited = false;
const LEGACY_ONLY = { prefer: "legacy" };
const ANDROID_INTERFACE_FIRST = new Set(["device/get", "token/get", "token/save", "token/delete"]);

const DEFAULT = {
    timeoutMs: 8000,
    // postMessage 표준 채널(있으면 우선 사용)
    androidChannels: ["AppBridge"],
    iosChannels: [],
    // 레거시 매핑: type -> { ifaceNames:[...], method:"...", callback:"..." }
    // (여기만 추가하면 계속 확장 가능)
    legacy: {
        "token/get":    { ifaceNames: ["AndroidToken"], method: "getToken",    callback: "setToken", arg: "none" },
        "token/delete": { ifaceNames: ["AndroidToken"], method: "deleteToken", callback: "deleteTokenResponse", arg: "none" },
        "token/save": { ifaceNames: ["AndroidToken"], method: "saveToken", callback: "saveTokenResponse", arg: "raw" },
        "device/get": { ifaceNames: ["AndroidDeviceInfo"], method: "getDeviceInfo", callback: "setDeviceInfo", arg: "none" },
        "location/get" : { ifaceNames: ["AndroidLocation"], method: "getGpsInfor", callback: "setGpsInfor", arg: "none" },
        "notiPermission/get" : { ifaceNames: ["AndroidNotification"], method: "getNotificationPermission", callback: "setNotificationPermission", arg: "none" },
        "photoPermission/get" : { ifaceNames: ["AndroidPermission"], method: "getPhotoLibraryPermission", callback: "setPhotoLibraryPermission", arg: "none" },
        "openGallery/open" : { ifaceNames: ["AndroidStorage"], method: "getFilePicker", callback: "setFilePicker", arg: "none" },
        "cameraPermission/get" : { ifaceNames: ["AndroidPermission"], method: "getCameraPermission", callback: "setCameraPermission", arg: "none" },
        "downloadFile/get" : { ifaceNames: ["AndroidDownload"], method: "getDownloadFiles", callback: "setDownloadFiles", arg: "raw" },
        "qrPage/get" : { ifaceNames: ["qrPageBridge"], method: "getQrPage", callback: "setQrPage", arg: "none" },
        "qrPage/delete" : { ifaceNames: ["qrPageBridge"], method: "deleteQrPage", callback: "deleteQrPageResponse", arg: "none" },
        // 애플 로그인: 네이티브 ASAuthorization 시트 → identityToken 반환(응답 setAppleLogin)
        "appleLogin/start" : { ifaceNames: ["AndroidAppleLogin"], method: "getAppleLogin", callback: "setAppleLogin", arg: "none" },
        // 예시)
        // "device/info":  { ifaceNames:["AndroidDeviceInfo"], method:"getDeviceInfo", callback:"setDeviceInfo" },
        // "location/get": { ifaceNames:["AndroidLocation"],   method:"getLocation",   callback:"onLocation"   },
    },
};

let CFG = { ...DEFAULT };

/* ───────── 유틸 ───────── */
const parseJSON = (raw) => {
    try { return typeof raw === "string" ? JSON.parse(raw) : raw; }
    catch { return raw; }
};
const nextId = () => `req_${Date.now()}_${Math.random().toString(16).slice(2)}`;

function on(type, handler) {
    if (!listeners.has(type)) listeners.set(type, new Set());
    listeners.get(type).add(handler);
    return () => listeners.get(type)?.delete(handler);
}
function emit(type, payload) {
    (listeners.get(type) || []).forEach(fn => {
        try { fn(payload); } catch (e) {
            // console.warn("bridge listener error:", e);
        }
    });
}

/* ───────── 응답 진입점(표준 & 레거시) ───────── */
function resolvePending(requestId, ok, data, error) {
    const entry = pending.get(requestId);
    if (!entry) return;
    pending.delete(requestId);
    ok ? entry.resolve(data) : entry.reject(new Error(error || "NativeError"));
}

// 표준: 네이티브가 호출 (dispatchResponse({requestId, ok, data, error}))
function mountStandardReceiver() {
    window.__bridge = window.__bridge || {};
    window.__bridge.dispatchResponse = function (raw) {
        const msg = parseJSON(raw);
        resolvePending(msg?.requestId, !!msg?.ok, msg?.data, msg?.error);
    };
}

// 레거시: 네이티브 전역 콜백을 공통 파이프로 연결
function mountLegacyReceiver(type, callbackName) {
    if (window[callbackName]) return; // 이미 다른 곳에서 정의했으면 존중
    window[callbackName] = (raw) => {
        const data = parseJSON(raw);
        // 1) 해당 type로 대기하는 요청이 있으면 그걸 우선 resolve
        const q = waitQueueByType.get(type) || [];
        if (q.length) {
            const requestId = q.shift();
            waitQueueByType.set(type, q);
            resolvePending(requestId, true, data, null);
            return;
        }
        // 2) 아니면 push 이벤트로 브로드캐스트 (실시간 알림류 대응)
        emit(type, data);
    };
}

/* ───────── 전송 경로 탐색 ───────── */
function findPostMessage() {
    const isIOS = !!window.webkit?.messageHandlers;
    // Android
    for (const name of CFG.androidChannels) {
        const obj = window[name];
        if (!isIOS && obj?.postMessage) {
            return (objToSend) =>
                obj.postMessage(typeof objToSend === "string" ? objToSend : JSON.stringify(objToSend));
        }
    }
    // iOS
    for (const name of CFG.iosChannels) {
        const handler = window.webkit?.messageHandlers?.[name];
        if (handler?.postMessage) return (objToSend) => handler.postMessage(objToSend);
    }
    return null;
}
function findLegacyInvoker(type, payload) {
    if (typeof window === "undefined") return null;

    const map = CFG?.legacy?.[type];
    if (!map) return null;

    const { ifaceNames, method, arg = "json" } = map;
    if (!Array.isArray(ifaceNames) || !method) return null;

    for (const ifname of ifaceNames) {
        const iface = window[ifname];
        if (iface && typeof iface[method] === "function") {
            return () => {
                if (arg === "json") {
                    iface[method](JSON.stringify(payload ?? {}));
                } else if (arg === "raw") {
                    const v = (typeof payload === "string") ? payload : JSON.stringify(payload ?? {});
                    iface[method](v);
                } else if (Array.isArray(arg)) {
                    iface[method](...(arg.map(k => payload?.[k])));
                } else {
                    iface[method](); // 인자 없음
                }
            };
        }
    }
    return null;
}
/* 초기화 */
export function initBridge(userCfg = {}) {
    if (inited) return;
    inited = true;
    CFG = {
        ...DEFAULT,
        ...userCfg,
        legacy: { ...DEFAULT.legacy, ...(userCfg.legacy || {}) },
        androidChannels: userCfg.androidChannels || DEFAULT.androidChannels,
        iosChannels: userCfg.iosChannels || DEFAULT.iosChannels,
    };

    // 표준 응답 수신기
    mountStandardReceiver();

    // 레거시 콜백들을 수신기로 등록
    Object.entries(CFG.legacy).forEach(([type, v]) => {
        if (v?.callback) mountLegacyReceiver(type, v.callback);
    });

    // (옵션) 개발 편의: window.Bridge로 노출
    window.Bridge = window.Bridge || { call, on };
}

/* ───────── 공통 호출 API ─────────
   - type: "token/get" | "token/delete" | ...
   - payload: 자유 JSON
   - opts: { timeoutMs?, prefer?: "postMessage"|"legacy" }
*/
export function call(type, payload = {}, opts = {}) {
    initBridge();
    const timeoutMs = opts.timeoutMs ?? CFG.timeoutMs;

    // 0) iOS면 먼저 iOS 하이브리드 시도(안드 경로 타다 실패하는 걸 방지)
    if (isIOS()) {
        const cb = nativeActionHandler();
        const map = CFG?.legacy?.[type];
        if (cb && map?.callback) {
            const requestId = nextId();
            const q = waitQueueByType.get(type) || [];
            q.push(requestId);
            waitQueueByType.set(type, q);

            return new Promise((resolve, reject) => {
                pending.set(requestId, {resolve, reject});
                try {
                    const action = map.action || map.method || type;
                    cb?.postMessage({ action, data: payload });

                } catch (e) {
                    const q2 = waitQueueByType.get(type) || [];
                    waitQueueByType.set(type, q2.filter(id => id !== requestId));
                    pending.delete(requestId);
                    reject(e);
                    return;
                }
                setTimeout(() => {
                    if (pending.has(requestId)) {
                        const q3 = waitQueueByType.get(type) || [];
                        waitQueueByType.set(type, q3.filter(id => id !== requestId));
                        pending.delete(requestId);
                        reject(new Error("Native timeout"));
                    }
                }, timeoutMs);
            });
        }
        // iOS에서 하이브리드 경로 없으면 계속 아래 공통 경로 시도
    }else{

        // 1) 표준 postMessage (있으면 공통으로 처리)
        //  ★ 2026-10-24 레거시 표에 있는 종류(device/get·token/get·권한 등)는 안드로이드 JS 인터페이스가 답한다.
        //    AppBridge 는 {action,data} 단방향 동작만 받아서, 여기로 보내면 답이 없어 8초 뒤 시간 초과가 났다
        //    (그래서 안드로이드 기기 토큰이 서버에 한 번도 등록되지 않아 푸시가 전혀 가지 않았다).
        const post = findPostMessage();
        //    위치·파일 선택 등은 지금 동작(웹 대체 경로)을 바꾸지 않도록 기기 정보·토큰만 옮긴다.
        const hasLegacy = ANDROID_INTERFACE_FIRST.has(type) && !!findLegacyInvoker(type, payload);
        if (post && opts.prefer !== "legacy" && !hasLegacy) {
            const requestId = nextId();
            return new Promise((resolve, reject) => {
                pending.set(requestId, { resolve, reject });
                try {
                    post(JSON.stringify({ type, payload, requestId }));
                } catch (e) {
                    pending.delete(requestId);
                    reject(e);
                    return;
                }
                const t = setTimeout(() => {
                    if (pending.has(requestId)) {
                        pending.delete(requestId);
                        reject(new Error("Native timeout"));
                    }
                }, timeoutMs);
                const entry = pending.get(requestId);
                if (entry) {
                    const wrap = (fn) => (v) => { clearTimeout(t); fn(v); };
                    pending.set(requestId, { resolve: wrap(resolve), reject: wrap(reject) });
                }
            });
        }

        // 2) ANDROID 레거시(JS 인터페이스)
        const invoke = findLegacyInvoker(type, payload);
        if (invoke) {
            const requestId = nextId();
            const q = waitQueueByType.get(type) || [];
            q.push(requestId);
            waitQueueByType.set(type, q);

            return new Promise((resolve, reject) => {
                pending.set(requestId, { resolve, reject });
                try {
                    invoke(); // arg: "raw"/"json"/"none" 처리는 findLegacyInvoker에서
                } catch (e) {
                    const q2 = waitQueueByType.get(type) || [];
                    waitQueueByType.set(type, q2.filter(id => id !== requestId));
                    pending.delete(requestId);
                    reject(e);
                    return;
                }
                setTimeout(() => {
                    if (pending.has(requestId)) {
                        const q3 = waitQueueByType.get(type) || [];
                        waitQueueByType.set(type, q3.filter(id => id !== requestId));
                        pending.delete(requestId);
                        reject(new Error("Native timeout"));
                    }
                }, timeoutMs);
            });
        }

    }



    return Promise.reject(new Error("No native route for type: " + type));
}

function isIOS() {
    return !!window.webkit?.messageHandlers;
}


function makeActionMessage(action, data){
    return JSON.stringify({ action, data });
}


function callLegacyAction(type, action, data, opts = {}){
    //IOS 일경우에는 data만 전달
    if(isIOS()){
        return call(type, data, { ...LEGACY_ONLY, ...opts });
    }else{
        return call(type, makeActionMessage(action, data), { ...LEGACY_ONLY, ...opts });
    }

}



/* ───────── 네이티브 단방향 액션 전송 핸들러 ─────────
   iOS: window.webkit.messageHandlers.callbackHandler (기존 그대로 우선)
   Android: window.AppBridge (JS 인터페이스, 문자열 JSON 수신)
   → 액션명·데이터 키는 양 플랫폼 100% 동일. 없으면 null 반환(호출부가 false 처리). */
function nativeActionHandler() {
    try {
        const cb = window.webkit?.messageHandlers?.callbackHandler;
        if (cb?.postMessage) return cb;
        const ab = window.AppBridge;
        if (ab?.postMessage) {
            return {
                postMessage: (m) => ab.postMessage(typeof m === "string" ? m : JSON.stringify(m)),
            };
        }
        return null;
    } catch (e) {
        return null;
    }
}

/* ───────── 편의 함수 ───────── */

/**
 * 사람이 화면을 보고 조작하는 호출의 제한시간.
 *
 * ⚠️ 기본 8초는 즉답용이다(토큰·디바이스정보). 애플 로그인 시트·권한 팝업·파일 선택은
 *    사용자가 읽고 Face ID 를 하거나 사진을 고르는 동안 8초를 쉽게 넘긴다.
 *    넘으면 웹이 "Native timeout" 으로 reject 해버려서, 사용자는 아직 시트를 보고 있는데
 *    화면에는 실패 문구가 뜬다. 그 뒤 진짜 응답이 와도 기다리는 요청이 없어 버려진다.
 *    (실제로 애플 로그인에서 '취소했는데 실패했습니다' 로 나타났다)
 *    네이티브는 성공·취소 어느 쪽이든 반드시 콜백을 주므로 넉넉히 기다리는 게 맞다.
 */
const USER_INTERACTIVE = { timeoutMs: 180000 };

export const appleLogin  = (opts) => call("appleLogin/start", {}, { ...USER_INTERACTIVE, ...opts }); //네이티브 애플 로그인 시트(→identityToken)
export const getToken    = (opts) => call("token/get", {}, opts); //토큰 가져오기
export const deleteToken = (opts) => call("token/delete", {}, opts); //토큰 삭제
export const getDevice    = (opts) => call("device/get", {}, opts); //디바이스 정보
export const getLocation    = (opts) => call("location/get", {}, { ...USER_INTERACTIVE, ...opts }); //위치정보 엑세스 권한 허용여부
export const getNotiPermission    = (opts) => call("notiPermission/get", {}, { ...USER_INTERACTIVE, ...opts }); //알림설정 권한 허용여부
export const getCameraPermission    = (opts) => call("cameraPermission/get", {}, { ...USER_INTERACTIVE, ...opts }); //알림설정 권한 허용여부
export const getPhotoPermission    = (opts) => call("photoPermission/get", {}, { ...USER_INTERACTIVE, ...opts }); //사진 권한 허용여부
export const openGallery    = (opts) => call("openGallery/open", {}, { ...USER_INTERACTIVE, ...opts }); //갤러리/파일 선택
// 네이티브 채팅방 열기(단방향). iOS callbackHandler 로 직접 전송, 값은 문자열(iOS data:[String:String]).
export const openChatRoom = (payload = {}) => {
    try {
        const data = {
            roomId: String(payload.roomId ?? ""),
            title: payload.title ?? "",
            spaceId: String(payload.spaceId ?? ""),
        };
        const handler = nativeActionHandler();
        console.log("🟦[Bridge] openChatRoom handler=", !!handler, "data=", JSON.stringify(data));
        if (!handler) return false;
        handler.postMessage({ action: "openChatRoom", data });
        return true;
    } catch (e) {
        console.log("🟦[Bridge] openChatRoom error=", e?.message);
        return false;
    }
};
// 네이티브 강사 프로필 화면 열기(커뮤니티/리뷰 등에서 작성자가 강사일 때 아바타 탭).
//  payload.teacherId = 강사(유저) id. TeacherProfileView(teacherId:) 로 풀스크린.
export const openTeacherProfile = (payload = {}) => {
    try {
        const handler = nativeActionHandler();
        if (!handler || payload?.teacherId == null || payload.teacherId === "") return false;
        handler.postMessage({ action: "openTeacherProfile", data: { teacherId: String(payload.teacherId) } });
        return true;
    } catch (e) {
        return false;
    }
};
// 네이티브 견적 자동응답 '메시지+금액 묶음' 관리 화면 열기(배민식). 완료 시 window.__onAutoQuoteChanged() 콜백.
export const openAutoQuoteTemplates = () => {
    try {
        const handler = nativeActionHandler();
        if (!handler) return false;
        handler.postMessage({ action: "openAutoQuoteTemplates", data: {} });
        return true;
    } catch (e) {
        return false;
    }
};
// 네이티브 자동견적 발송 내역 화면 열기(어느 요청에 자동견적이 나갔는지 + 횟수 소진으로 못 보낸 요청).
//  payload.month = 'YYYY-MM'(선택, 없으면 이번 달). 액션명·데이터 키는 iOS/AOS 와 동일.
export const openAutoQuoteHistory = (payload = {}) => {
    try {
        const handler = nativeActionHandler();
        if (!handler) return false;
        const month = typeof payload?.month === "string" && /^\d{4}-\d{2}$/.test(payload.month) ? payload.month : null;
        handler.postMessage({ action: "openAutoQuoteHistory", data: month ? { month } : {} });
        return true;
    } catch (e) {
        return false;
    }
};
// 네이티브 학원 공개 프로필 화면 열기(채용공고 학원명 탭). payload.academyId = 학원(유저) id.
//  AcademyProfileView(academyId:) 로 풀스크린. 강사 프로필(openTeacherProfile)과 동일 패턴.
export const openAcademyProfile = (payload = {}) => {
    try {
        const handler = nativeActionHandler();
        if (!handler || payload?.academyId == null || payload.academyId === "") return false;
        handler.postMessage({
            action: "openAcademyProfile",
            data: {
                academyId: String(payload.academyId),
                ...(payload?.src ? { src: String(payload.src) } : {}),
            },
        });
        return true;
    } catch (e) {
        return false;
    }
};
// 네이티브 [학원] 소속 강사 관리 열기 — 학원 코드·신청 승인/거절·해지. openTeacherProfile 과 동일 패턴.
//  완료 시 window.__onAcademyTeachersChanged?.() 로 웹 목록 갱신.
export const openAcademyTeachers = () => {
    try {
        const handler = nativeActionHandler();
        if (!handler) return false;
        handler.postMessage({ action: "openAcademyTeachers", data: {} });
        return true;
    } catch (e) {
        return false;
    }
};
// 네이티브 [강사] 학원 연결 열기 — 학원 코드 입력·신청 취소 + 소속 학원 목록.
//  푸시(ACADEMY_JOIN_APPROVED / ACADEMY_JOIN_DENIED) 딥링크도 이 화면으로 들어온다.
//  ★ action 이름은 바꾸지 않는다 — 이미 배포된 앱의 브릿지 분기가 이 문자열에 묶여 있다.
export const openAcademyInvites = () => {
    try {
        const handler = nativeActionHandler();
        if (!handler) return false;
        handler.postMessage({ action: "openAcademyInvites", data: {} });
        return true;
    } catch (e) {
        return false;
    }
};
// 네이티브 멤버십(구독/이용권) 화면 열기 — 이용권 종류 선택 시. featureType 전달.
//  네이티브가 잡플래닛식 구독 화면 표시 → 플랜 선택 시 window.__onMembershipSelected(planCode) 콜백 → 웹에서 결제.
export const openMembership = (payload = {}) => {
  try {
    const handler = nativeActionHandler();
    if (!handler) return false;
    // 멤버십은 계정에 하나다(2026-08-30 개편). 회원유형은 더 이상 상품을 가르지 않는다.
    //  memberType 은 구버전 앱 호환을 위해 남겨 보내되, 서버가 무시한다.
    handler.postMessage({
      action: "openMembership",
      data: {
        featureType: String(payload.featureType || "MEMBERSHIP"),
        memberType: String(payload.memberType || ""),
      },
    });
    return true;
  } catch (e) {
    return false;
  }
};

// 앱에서는 웹 성과 페이지 대신 독립된 SwiftUI 그래프 화면을 연다.
export const openMembershipPerformance = (payload = {}) => {
  try {
    const handler = nativeActionHandler();
    if (!handler) return false;
    handler.postMessage({
      action: "openMembershipPerformance",
      data: { memberType: String(payload.memberType || "") },
    });
    return true;
  } catch (e) {
    return false;
  }
};

// 네이티브 견적 문진 위저드 열기(웹 "견적 요청" 버튼 → 네이티브 풀스크린).
//  payload.category = 카테고리 id 또는 이름(예: "ballet"/"발레").
//  payload.targetTeacherId = 지정 견적요청(강사 1명에게만). 없으면 브로드캐스트.
export const openQuoteWizard = (payload = {}) => {
    try {
        const data = { category: String(payload.category ?? "") };
        if (payload.targetTeacherId != null && payload.targetTeacherId !== "") {
            data.targetTeacherId = String(payload.targetTeacherId);
        }
        const handler = nativeActionHandler();
        if (!handler) return false;
        handler.postMessage({ action: "openQuoteWizard", data });
        return true;
    } catch (e) {
        return false;
    }
};
// 네이티브 주소(관심지역) 설정 온보딩 열기. 완료 시 window.onAddressSelected(region) 콜백.
export const openAddressSetup = () => {
    try {
        const handler = nativeActionHandler();
        if (!handler) return false;
        handler.postMessage({ action: "openAddressSetup", data: {} });
        return true;
    } catch (e) {
        return false;
    }
};
// 네이티브 약관동의 화면 열기(회원가입 진입 전 or 로그인 후 미동의 게이트). 강제 풀커버.
//  동의 시 window.__onSignupTermsAgreed(agreementsJson), 미동의/뒤로 시 window.__onSignupTermsDeclined().
export const openSignupTerms = (payload = {}) => {
    try {
        const handler = nativeActionHandler();
        if (!handler) return false;
        handler.postMessage({ action: "openSignupTerms", data: { mode: String(payload.mode || "signup") } });
        return true;
    } catch (e) {
        return false;
    }
};
// 네이티브 회원유형 선택 온보딩 열기(가입완료 후, 강제). 선택 시 window.__onRoleSelected(role) 콜백.
export const openRoleOnboarding = () => {
    try {
        const handler = nativeActionHandler();
        if (!handler) return false;
        handler.postMessage({ action: "openRoleOnboarding", data: {} });
        return true;
    } catch (e) {
        return false;
    }
};
// 네이티브 역할 인증 서류 첨부 화면 열기(사업역할 온보딩/인증유도). 완료 시 window.__onRoleVerify(role, urlsJson).
//  payload 는 { role, ownerName? } 또는 role 문자열(구 호출부 호환).
//  ownerName = 본인확인으로 등록된 실명. 학원 인증 화면이 '대표원장'으로 읽기전용 표시한다.
export const openRoleVerification = (payload) => {
    try {
        const handler = nativeActionHandler();
        if (!handler) return false;
        const role = typeof payload === "string" ? payload : payload?.role;
        const ownerName = typeof payload === "string" ? "" : payload?.ownerName;
        handler.postMessage({
            action: "openRoleVerification",
            data: { role: String(role || ""), ownerName: String(ownerName || "") },
        });
        return true;
    } catch (e) {
        return false;
    }
};
// 회원유형 관리(넷플릭스식) 네이티브 화면 열기. 웹이 현재 역할데이터 + 히어로 배경(서버가 고른 랜덤 1장) 전달.
//  네이티브 액션(추가/전환)→window.__onRoleComplete(role,urlsJson), 해제→window.__onRoleRemove(role).
export const openRoleManage = (payload) => {
    try {
        const handler = nativeActionHandler();
        if (!handler) return false;
        handler.postMessage({
            action: "openRoleManage",
            data: {
                roles: JSON.stringify(payload?.roles ?? { held: [], roles: [] }),
                heroImage: String(payload?.heroImage ?? ""),
                activeType: String(payload?.activeType ?? "GENERAL"),
            },
        });
        return true;
    } catch (e) {
        return false;
    }
};
// 활성유형 변경 통지(웹 → 네이티브) — 네이티브 X-Active-Type 헤더 동기화.
//  역방향(네이티브 전환 → 웹)은 window.__onActiveTypeChanged 콜백(AuthContext 등록).
export const syncActiveTypeNative = (type, changed = false) => {
    try {
        const handler = nativeActionHandler();
        if (!handler) return false;
        handler.postMessage({
            action: "syncActiveType",
            // changed=true 일 때만 네이티브가 전환 토스트 표시(초기 동기화는 무음)
            data: { type: String(type || "GENERAL"), changed: changed ? "1" : "0" },
        });
        return true;
    } catch (e) {
        return false;
    }
};

// 회원유형이 바뀜(인증 서류 제출·역할 추가·해제 완료) 통지 → 네이티브 플로팅(인증 책갈피 '심사중' 등) 1회 재조회.
//  서버는 '본인 제출'에 소켓 이벤트를 보내지 않으므로 웹이 제출을 끝낸 시점에 직접 알린다(시간 기반 재조회 대체).
export const notifyRolesChanged = () => {
    try {
        const handler = nativeActionHandler();
        if (!handler) return false;
        handler.postMessage({ action: "rolesChanged", data: {} });
        return true;
    } catch (e) {
        return false;
    }
};

// 강사 리뷰 [수정] → 네이티브 리뷰 수정 바텀시트. 저장 시 window.__onReviewEdited(rating, content) 콜백.
export const openReviewEdit = (payload) => {
    try {
        const handler = nativeActionHandler();
        if (!handler) return false;
        handler.postMessage({
            action: "openReviewEdit",
            data: {
                rating: String(payload?.rating ?? 0),
                content: String(payload?.content ?? ""),
            },
        });
        return true;
    } catch (e) {
        return false;
    }
};
// 웹 모달 표시/해제 → 네이티브 플로팅(채팅·인증 책갈피) 숨김/표시(모달 X버튼 가림 방지).
export const setFloatingHidden = (hidden) => {
    try {
        nativeActionHandler()?.postMessage({
            action: "setFloatingHidden", data: { hidden: hidden ? "1" : "0" },
        });
    } catch (e) {}
};
// 현재 프론트 라우트(path)를 네이티브에 통지 → 네이티브가 플로팅 채팅버튼 노출 화이트리스트 판정.
export const notifyRoute = (path) => {
    try {
        nativeActionHandler()?.postMessage({
            action: "routeChanged", data: { path: String(path || "") },
        });
    } catch (e) {}
};
// 웹이 window.__muyeonResult 를 등록했음을 네이티브에 1회 통지(페이지 로드당 1번).
//  AOS 는 이 신호를 받으면 evalWhenReady(300ms 재시도 루프)를 멈추고 바로 결과 콜백을 부른다.
let webReadySent = false;
export const notifyWebReady = () => {
    if (webReadySent) return;
    try {
        const handler = nativeActionHandler();
        if (!handler) return;
        handler.postMessage({ action: "webReady", data: {} });
        webReadySent = true;
    } catch (e) {}
};
// 네이티브 레슨 일정 캘린더 열기 (MY '레슨 일정' / 하루전 푸시 딥링크).
// payload.lessonId(+highlight) 를 실으면 네이티브 캘린더가 해당 일정을 강조(알림목록 탭 딥링크).
export const openLessonCalendar = (payload) => {
    try {
        const data = {};
        if (payload?.lessonId != null) data.lessonId = String(payload.lessonId);
        if (payload?.highlight) data.highlight = "1";
        nativeActionHandler()?.postMessage({ action: "openLessonCalendar", data });
    } catch (e) {}
};
// 레슨 예약 네이티브 바텀시트(회원). payload.lessonProductId = 예약할 레슨 상품.
export const openLessonBooking = (payload) => {
    try {
        nativeActionHandler()?.postMessage({
            action: "openLessonBooking",
            data: { lessonProductId: String(payload?.lessonProductId ?? "") },
        });
        return true;
    } catch (e) { return false; }
};
// 레슨 예약 상세(캐치테이블식 네이티브 시트). payload.reservationId = 내 예약 id.
export const openLessonReservationDetail = (payload) => {
    try {
        nativeActionHandler()?.postMessage({
            action: "openLessonReservationDetail",
            data: { reservationId: String(payload?.reservationId ?? "") },
        });
        return true;
    } catch (e) { return false; }
};
// 레슨 예약 시간 관리(강사/원장). 내 레슨 상품별 반복 시간 규칙 등록/삭제 + 캘린더 미리보기.
export const openLessonSlotManage = (payload) => {
    try {
        nativeActionHandler()?.postMessage({
            action: "openLessonSlotManage",
            data: { lessonProductId: String(payload?.lessonProductId ?? "") },
        });
        return true;
    } catch (e) { return false; }
};
// 공간 상세(네이티브). payload.spaceId 필수, date=목록에서 고른 날짜, fromReservation=예약내역 진입.
export const openSpaceDetail = (payload) => {
    try {
        if (payload?.spaceId == null) return false;
        const data = { spaceId: String(payload.spaceId) };
        if (payload?.date) data.date = String(payload.date);
        if (payload?.fromReservation) data.fromReservation = "1";
        nativeActionHandler()?.postMessage({ action: "openSpaceDetail", data });
        return true;
    } catch (e) { return false; }
};
// ★ 2026-11-03 무용센터 양도양수·공간 대관(새 앱 1.1.5+ 네이티브). 지원 여부는 nativeRoute 의 capability 로 판단.
const postAction = (action, data = {}) => {
    try {
        nativeActionHandler()?.postMessage({ action, data });
        return true;
    } catch (e) { return false; }
};
export const openStudioTransfers = () => postAction("openStudioTransfers");
export const openStudioTransferDetail = (p) => (p?.id == null ? false : postAction("openStudioTransferDetail", { id: String(p.id) }));
export const openStudioTransferEditor = (p = {}) => postAction("openStudioTransferEditor", p?.id != null ? { id: String(p.id) } : {});
export const openMyStudioTransfers = () => postAction("openMyStudioTransfers");
export const openSpaces = () => postAction("openSpaces");
export const openSpaceEditor = (p = {}) => postAction("openSpaceEditor", p?.id != null ? { id: String(p.id) } : {});
export const openMySpaces = () => postAction("openMySpaces");
export const openMySpaceReservations = () => postAction("openMySpaceReservations");
export const openMySpaceBookings = () => postAction("openMySpaceBookings");
// 네이티브 회원 조회·수강권(강사/학원). 성공 시 true.
export const openStudioMembers = (payload = {}) => {
    try {
        nativeActionHandler()?.postMessage({
            action: "openStudioMembers", data: {},
        });
        return true;
    } catch (e) { return false; }
};
// 네이티브 매출 현황(강사/학원). 성공 시 true.
export const openStudioSales = (payload = {}) => {
    try {
        nativeActionHandler()?.postMessage({
            action: "openStudioSales", data: {},
        });
        return true;
    } catch (e) { return false; }
};
// 네이티브 일정 관리(강사/학원). 성공 시 true.
export const openStudioSchedule = (payload = {}) => {
    try {
        nativeActionHandler()?.postMessage({
            action: "openStudioSchedule", data: {},
        });
        return true;
    } catch (e) { return false; }
};
// 네이티브 알림함. 성공 시 true.
export const openNotifications = (payload = {}) => {
    try {
        nativeActionHandler()?.postMessage({
            action: "openNotifications", data: {},
        });
        return true;
    } catch (e) { return false; }
};
// 네이티브 알림 수신 설정(종류별 푸시 on/off). 성공 시 true.
//  ⚠️ 앱에는 같은 화면이 웹(/notificationSettings)에도 있다. 앱에서 웹 화면이 열리면
//     알림함 톱니가 여는 네이티브 화면과 두 모습이 되므로, 앱이면 반드시 이쪽으로 보낸다.
export const openNotificationSettings = (payload = {}) => {
    try {
        nativeActionHandler()?.postMessage({
            action: "openNotificationSettings", data: {},
        });
        return true;
    } catch (e) { return false; }
};
// 네이티브 '레슨·견적 관리' 그리드 허브. 성공 시 true.
export const openLessonQuoteHub = (payload = {}) => {
    try {
        // role(활동유형) 을 넘겨 네이티브 허브 제목에 '(강사/학원)' 표시.
        nativeActionHandler()?.postMessage({ action: "openLessonQuoteHub", data: { role: String(payload?.role || "") } });
        return true;
    } catch (e) { return false; }
};
// 네이티브 '스튜디오 운영' 그리드 허브(회원·매출·일정). 성공 시 true.
export const openStudioOps = (payload = {}) => {
    try {
        nativeActionHandler()?.postMessage({ action: "openStudioOps", data: {} });
        return true;
    } catch (e) { return false; }
};
// 네이티브 웹 오버레이 모달(허브 위 /myLessons 등)에서 '뒤로가기(루트)' → 네이티브가 모달을 닫고 허브로 복귀.
//  window.__nativeModal(네이티브가 documentStart 에 주입) 일 때만 의미. 성공 시 true.
export const closeModal = () => {
    try {
        nativeActionHandler()?.postMessage({ action: "closeModal", data: {} });
        return true;
    } catch (e) { return false; }
};
// 네이티브 레슨 개설/수정 위저드(payload.lessonId 있으면 수정). 성공 시 true.
export const openLessonCreate = (payload = {}) => {
    try {
        nativeActionHandler()?.postMessage({
            action: "openLessonCreate",
            data: { lessonId: payload?.lessonId != null ? String(payload.lessonId) : "" },
        });
        return true;
    } catch (e) { return false; }
};
// 네이티브 채팅방 "목록" 열기. payload.filter=활성유형 기준 기본 세그먼트(all|requested|responded|inquiry).
export const openChatList = (payload) => {
    try {
        nativeActionHandler()?.postMessage({
            action: "openChatList", data: { filter: String(payload?.filter || "all") },
        });
    } catch (e) {}
};
// 네이티브 견적 허브 열기. payload.isPro=강사/원장이면 '보낸 견적' 탭 노출,
//  payload.tab="sent"이면 보낸 견적 탭으로 바로 진입(강사 채택 알림). 성공 시 true.
export const openMyQuotes = (payload) => {
    try {
        const handler = nativeActionHandler();
        if (!handler) return false;
        handler.postMessage({
            action: "openMyQuotes",
            data: { isPro: payload?.isPro ? "1" : "0", tab: payload?.tab === "sent" ? "sent" : "" },
        });
        return true;
    } catch (e) {
        return false;
    }
};
// 네이티브 '견적 모아보기'(강사) — 그리드+덱 스와이프 카드 풀스크린. 성공 시 true.
// 이력서 네이티브 전환(프로필 구조 개선안 2026-07-30)
const simpleBridge = (action, data = {}) => {
    try {
        const handler = nativeActionHandler();
        if (!handler) return false;
        handler.postMessage({ action, data });
        return true;
    } catch (e) {
        return false;
    }
};
// payload.mode="dancer" → 네이티브 무용수 프로필(간소화+포트폴리오). 없으면 강사 이력서.
export const openResumeList = (payload = {}) =>
    simpleBridge("openResumeList", payload.mode ? { mode: String(payload.mode) } : {});
export const openResumeEdit = (payload = {}) =>
    simpleBridge("openResumeEdit", {
        ...(payload.resumeId ? { resumeId: String(payload.resumeId) } : {}),
        ...(payload.mode ? { mode: String(payload.mode) } : {}),
        ...(payload.seekProfile === true || String(payload.seekProfile) === "1" ? { seekProfile: "1" } : {}),
    });
export const openFieldVisibility = (payload = {}) => simpleBridge("openFieldVisibility", {
    ...(payload.mode || payload.as ? { mode: payload.mode === "dancer" || payload.as === "DANCER" ? "dancer" : "teacher" } : {}),
});
// ★ 10-26 '캐스팅·무대' 네이티브 화면(배너·아이콘 칸·섹션 가로 카드). 앱이 nativeCastingStage 를 알려 줄 때만 쓴다(nativeRoute).
// payload(선택, 10-27 링크): castingId | screen: "castingList"·"issues" | issueNo(+category) — 그 화면부터 연다.
export const openCastingStage = (payload = {}) => simpleBridge("openCastingStage", payload);
// 알림 허용 안내 — 앱이 아직 묻지 않았을 때만 띄운다(iOS·AOS PushPrimer). 결과는 window.__onNotificationConsent.
export const openNotificationConsent = () => simpleBridge("openNotificationConsent");
// ★ 2026-10-31 하단 MY 탭 길게 누르기 → 네이티브 유형 바텀시트(인스타 계정 전환처럼). 앱이 activeTypeSheet 를 알려 줄 때만.
//  types: [{ type, label }] JSON 문자열, current: 지금 유형. 고른 값은 window.__onActiveTypePicked(type | "MANAGE").
//  전환 자체(서버 저장·헤더 동기·토스트)는 웹 AuthContext.setActiveType 이 한다 — 네이티브는 고르기만.
export const openActiveTypeSheet = (payload = {}) =>
    simpleBridge("openActiveTypeSheet", {
        types: JSON.stringify(payload.types || []),
        current: String(payload.current || ""),
        // ★ 2026-10-31 새 소식이 있는 다른 유형(쉼표) — 시트 줄에 빨간 점. 옛 앱은 읽지 않는다.
        newTypes: (payload.newTypes || []).join(","),
    });
// ★ 2026-10-31 학원·공연팀 활동유형이면 mode 를 안 줘도 채용 모드(recruit) — 홈 '추천 강사' 등 일부 화면이
//  mode 없이 불러 앱 프로필 하단이 [레슨 문의]로 떴다. 웹 TeacherDetail(isRecruiter)·TeacherList 와 같은 규칙.
const RECRUITER_TYPES = ["ACADEMY", "TEAM"];
export const openPublicProfile = (payload = {}) => {
    const mode = payload.mode || (RECRUITER_TYPES.includes(getActiveTypeHeader()) ? "recruit" : "");
    return simpleBridge("openPublicProfile", {
        userId: String(payload.userId || ""),
        ...(payload.preview ? { preview: "1" } : {}),
        ...(mode ? { mode: String(mode) } : {}),
        ...(payload.src ? { src: String(payload.src) } : {}),
    });
};
export const openApplicantResume = (payload = {}) =>
    simpleBridge("openApplicantResume", {
        jobId: String(payload.jobId || ""),
        subId: String(payload.subId || ""),
        applicationKind: payload.subId ? "SUB" : "JOB",
        applicationId: String(payload.applicationId || ""),
    });

// 웹 '후기 작성' → 네이티브 리뷰 작성(3축). teacherId 필수, 나머지는 있으면 프리필.
export const openReviewWrite = (payload = {}) =>
    simpleBridge("openReviewWrite", {
        teacherId: String(payload.teacherId || ""),
        ...(payload.teacherName ? { teacherName: String(payload.teacherName) } : {}),
        ...(payload.teacherImage ? { teacherImage: String(payload.teacherImage) } : {}),
        ...(payload.dateLine ? { dateLine: String(payload.dateLine) } : {}),
        ...(payload.lessonType ? { lessonType: String(payload.lessonType) } : {}),
    });

// 웹 '내가 쓴 리뷰/전체 후기' → 네이티브 전체 리뷰 화면. teacherId 필수.
export const openReviewList = (payload = {}) =>
    simpleBridge("openReviewList", { teacherId: String(payload.teacherId || "") });

// 웹 MY '채용 공고 등록/수정' → 네이티브 내 공고 관리
export const openMyJobPostings = () => simpleBridge("openMyJobPostings");

export const openQuoteBrowse = () => {
    try {
        const handler = nativeActionHandler();
        if (!handler) return false;
        handler.postMessage({ action: "openQuoteBrowse", data: {} });
        return true;
    } catch (e) {
        return false;
    }
};
// 네이티브 풀스크린 이미지 뷰어. 기본은 열람 전용이며 소유자 화면만 allowSave=true를 명시한다.
export const openImageViewer = (payload) => {
    try {
        const handler = nativeActionHandler();
        if (!handler) return false;
        const urls = (payload?.urls || []).filter(Boolean);
        if (!urls.length) return false;
        handler.postMessage({
            action: "openImageViewer",
            // JSON 배열 문자열 — URL 에 콤마가 있어도 안전(네이티브가 JSON 우선 파싱, 콤마 폴백)
            data: {
                urls: JSON.stringify(urls),
                index: String(payload?.index || 0),
                allowSave: payload?.allowSave === true ? "true" : "false",
            },
        });
        return true;
    } catch (e) {
        return false;
    }
};
// 네이티브 '받은 견적' 상세 열기. payload.quoteId (+ payload.r = 방금 온 견적 강조)
export const openReceivedQuotes = (payload) => {
    try {
        const handler = nativeActionHandler();
        if (!handler || !payload?.quoteId) return false;
        const data = { quoteId: String(payload.quoteId) };
        if (payload.r) data.r = String(payload.r);
        handler.postMessage({ action: "openReceivedQuotes", data });
        return true;
    } catch (e) {
        return false;
    }
};
export const saveToken = ({ accessToken, refreshToken }, opts = {}) => { //토큰저장
    return callLegacyAction("token/save", "saveToken", { accessToken, refreshToken }, opts);
};
export const downloadFile = (payload, opts = {}) => { //파일 다운로드 발송
    return callLegacyAction("downloadFile/get", "getDownloadFiles", payload, opts);
};

export const getQrPage    = (opts) => call("qrPage/get", {}, opts); //qr 페이지 이동값 가져오기
export const deleteQrPage = (opts) => call("qrPage/delete", {}, opts); //qr 페이지 이동값 삭제

// push(방향성 알림) 구독
export { on };
